"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { SiteSettings, defaultSettings, fetchSiteSettings } from '@/lib/site-settings';

type SiteSettingsContextType = {
  settings: SiteSettings;
  isLoading: boolean;
  refreshSettings: () => Promise<void>;
};

// Create context with default values
const SiteSettingsContext = createContext<SiteSettingsContextType>({
  settings: defaultSettings,
  isLoading: true,
  refreshSettings: async () => {}
});

// Hook to use site settings
export const useSiteSettings = () => useContext(SiteSettingsContext);

// Provider component
export function SiteSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);

  // Function to load or refresh settings
  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const siteSettings = await fetchSiteSettings();
      setSettings(siteSettings);
    } catch (error) {
      console.error('Error loading site settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load settings on first render
  useEffect(() => {
    loadSettings();
  }, []);

  return (
    <SiteSettingsContext.Provider 
      value={{ 
        settings, 
        isLoading,
        refreshSettings: loadSettings
      }}
    >
      {children}
    </SiteSettingsContext.Provider>
  );
}
